import { useState, useEffect } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { LayoutDashboard, Package, ShoppingBag, Heart, Star, ChevronRight, LogOut, Plus, ArrowLeft, MessageCircle, MessageSquare, UserCog, ShieldAlert, Award, ShieldCheck, Handshake, ReceiptText, Crown, Store, Radio, Wallet, Gift, Shirt, Truck, Zap, MapPin, Lock, Percent, BadgeDollarSign, Image as ImageIcon, Menu, X, ListPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { useI18n } from '@/context/I18nContext';
import { toast } from 'sonner';

const NavItem = ({ to, icon: Icon, label, end, testId, onClick }) => (
    <NavLink to={to} end={end} data-testid={testId} onClick={onClick}
        className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? 'bg-[hsl(var(--secondary))] text-foreground border-l-2 border-[hsl(var(--primary))] pl-2' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'}`}>
        <Icon className="h-4 w-4" />
        <span className="flex-1">{label}</span>
        <ChevronRight className="h-3 w-3 opacity-40" />
    </NavLink>
);

const LockedNavItem = ({ icon: Icon, label, testId }) => (
    <Link to="/planes" data-testid={testId}
        onClick={() => toast.info('Activa esta función desde tu Plan OkGo')}
        className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-muted-foreground/50 hover:bg-secondary/50 transition-colors">
        <Icon className="h-4 w-4 opacity-60" />
        <span className="flex-1 opacity-70">{label}</span>
        <div className="flex items-center gap-1 bg-gradient-to-r from-amber-500/15 to-orange-500/15 border border-amber-400/30 rounded-full px-1.5 py-0.5 shrink-0">
            <Lock className="h-2.5 w-2.5 text-amber-600" />
            <span className="text-[9px] text-amber-700 font-bold">Pro</span>
        </div>
    </Link>
);

export default function DashboardLayout() {
    const { user, logout, role } = useAuth();
    const nav = useNavigate();
    const { t } = useI18n();
    const [planFeatures, setPlanFeatures] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        if (role === 'user') {
            api.get('/me/plan')
                .then(({ data }) => setPlanFeatures(data?.plan?.features || data?.features || {}))
                .catch(() => setPlanFeatures({}));
        }
    }, [role]);

    const isLocked = (feature) => planFeatures !== null && !planFeatures[feature];
    const closeMenu = () => setMenuOpen(false);

    // Shared nav items JSX — used in both desktop sidebar and mobile drawer
    const navItems = (
        <nav className="space-y-1">
            <NavItem to="/dashboard" end icon={LayoutDashboard} label={t('nav.dashboard','Resumen')} testId="nav-dashboard" onClick={closeMenu} />
            <NavItem to="/dashboard/ads" icon={Package} label={t('nav.my_ads','Mis anuncios')} testId="nav-ads" onClick={closeMenu} />
            {isLocked('okstore_enabled')
                ? <LockedNavItem icon={Store} label="Mi OkStore" testId="nav-storefront" />
                : <NavItem to="/dashboard/storefront" icon={Store} label="Mi OkStore" testId="nav-storefront" onClick={closeMenu} />}
            {isLocked('oklive_enabled')
                ? <LockedNavItem icon={Radio} label="OkLive Shop" testId="nav-livestream" />
                : <NavItem to="/dashboard/livestream" icon={Radio} label="OkLive Shop" testId="nav-livestream" onClick={closeMenu} />}
            <NavItem to="/dashboard/wallet" icon={Wallet} label="OkWallet" testId="nav-wallet" onClick={closeMenu} />
            <NavItem to="/dashboard/cashback" icon={Gift} label="Cashback" testId="nav-cashback" onClick={closeMenu} />
            <NavItem to="/dashboard/service-orders" icon={Shirt} label="Servicios contratados" testId="nav-service-orders" onClick={closeMenu} />
            <NavItem to="/dashboard/envios" icon={Truck} label="OkEnvíos" testId="nav-envios" onClick={closeMenu} />
            {isLocked('flash_sales_enabled')
                ? <LockedNavItem icon={Zap} label="Ofertas relámpago" testId="nav-flash-sales" />
                : <NavItem to="/dashboard/flash-sales" icon={Zap} label="Ofertas relámpago" testId="nav-flash-sales" onClick={closeMenu} />}
            <NavItem to="/dashboard/purchases" icon={ShoppingBag} label={t('nav.purchases','Mis compras')} testId="nav-purchases" onClick={closeMenu} />
            <NavItem to="/dashboard/sales" icon={ShoppingBag} label={t('nav.sales','Mis ventas')} testId="nav-sales" onClick={closeMenu} />
            <NavItem to="/dashboard/offers" icon={Handshake} label={t('nav.offers','Ofertas')} testId="nav-offers" onClick={closeMenu} />
            <NavItem to="/dashboard/favorites" icon={Heart} label={t('nav.favorites','Favoritos')} testId="nav-favorites" onClick={closeMenu} />
            <NavItem to="/dashboard/lists" icon={ListPlus} label="Mis listas" testId="nav-lists" onClick={closeMenu} />
            <NavItem to="/dashboard/messages" icon={MessageCircle} label={t('nav.messages','Mensajes')} testId="nav-messages" onClick={closeMenu} />
            <NavItem
                to="#"
                icon={MessageSquare}
                label="OkChat"
                testId="nav-okchat"
                onClick={(e) => { e.preventDefault(); if (window.okChatOpen) window.okChatOpen({}); closeMenu(); }}
            />
            <NavItem to="/dashboard/invoices" icon={ReceiptText} label={t('nav.invoices','Facturas')} testId="nav-invoices" onClick={closeMenu} />
            <NavItem to="/dashboard/kyc" icon={ShieldCheck} label={t('nav.kyc','Verificación')} testId="nav-kyc" onClick={closeMenu} />
            <NavItem to="/planes" icon={Crown} label="Mi Plan OkGo" testId="nav-okgo-plans" onClick={closeMenu} />
            <NavItem to="/dashboard/services-provider" icon={Shirt} label="Mis servicios" testId="nav-my-services" onClick={closeMenu} />
            <NavItem to="/dashboard/addresses" icon={MapPin} label="Direcciones" testId="nav-addresses" onClick={closeMenu} />
            <NavItem to="/dashboard/comisiones" icon={Percent} label="Comisiones" testId="nav-comisiones" onClick={closeMenu} />
            <NavItem to="/dashboard/impuestos" icon={BadgeDollarSign} label="Impuestos" testId="nav-impuestos" onClick={closeMenu} />
            {isLocked('okpro_enabled')
                ? <LockedNavItem icon={Award} label="OkPro · Super Boost" testId="nav-okpro" />
                : <NavItem to="/dashboard/okpro" icon={Award} label="OkPro · Super Boost" testId="nav-okpro" onClick={closeMenu} />}
            {isLocked('banners_enabled')
                ? <LockedNavItem icon={ImageIcon} label="Mis banners" testId="nav-seller-banners" />
                : <NavItem to="/dashboard/seller-banners" icon={ImageIcon} label="Mis banners" testId="nav-seller-banners" onClick={closeMenu} />}
            <NavItem to="/dashboard/moderation" icon={ShieldAlert} label={t('nav.moderation','Moderación')} testId="nav-moderation" onClick={closeMenu} />
            <NavItem to="/dashboard/ratings" icon={Star} label={t('nav.ratings','Calificaciones')} testId="nav-ratings" onClick={closeMenu} />
            <NavItem to="/dashboard/benefits" icon={Award} label={t('nav.benefits','Beneficios')} testId="nav-benefits" onClick={closeMenu} />
            <NavItem to="/dashboard/profile" icon={UserCog} label={t('nav.profile','Editar perfil')} testId="nav-profile" onClick={closeMenu} />
        </nav>
    );

    const userBlock = (
        <div className="flex items-center gap-3 pb-4 mb-3 border-b">
            <div className="h-10 w-10 rounded-full bg-[hsl(var(--accent))] flex items-center justify-center font-semibold shrink-0">
                {user?.nickname?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="min-w-0">
                <div className="text-sm font-semibold truncate">@{user?.nickname}</div>
                <div className="text-xs text-muted-foreground">Panel de usuario</div>
            </div>
        </div>
    );

    const logoutBtn = (
        <div className="mt-4 border-t pt-3">
            <button onClick={() => { logout(); nav('/'); closeMenu(); }}
                className="w-full flex items-center gap-2 text-sm text-muted-foreground hover:text-destructive px-3 py-2 rounded-lg"
                data-testid="dash-logout">
                <LogOut className="h-4 w-4" />Cerrar sesión
            </button>
        </div>
    );

    return (
        <div className="min-h-screen bg-[hsl(var(--ok-surface-2))]/40">

            {/* ── Mobile top bar (visible below lg) ── */}
            <div className="lg:hidden sticky top-0 z-40 bg-card/95 backdrop-blur border-b shadow-sm px-4 py-3 flex items-center justify-between">
                <button
                    onClick={() => setMenuOpen(true)}
                    aria-label="Abrir menú"
                    className="h-9 w-9 flex items-center justify-center rounded-xl border bg-background hover:bg-secondary transition-colors"
                >
                    <Menu className="h-5 w-5" />
                </button>
                <Link to="/" className="text-sm font-bold tracking-tight text-foreground">Ok lo Vendo</Link>
                <Button size="sm" onClick={() => nav('/dashboard/ads/new')} className="h-8 bg-red-600 hover:bg-red-700">
                    <Plus className="h-4 w-4" />
                </Button>
            </div>

            {/* ── Mobile slide-in drawer ── */}
            {menuOpen && (
                <>
                    {/* Backdrop */}
                    <div
                        className="lg:hidden fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
                        onClick={closeMenu}
                        aria-hidden="true"
                    />
                    {/* Drawer */}
                    <div className="lg:hidden fixed left-0 top-0 bottom-0 z-50 w-72 bg-card shadow-2xl overflow-y-auto flex flex-col">
                        <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
                            <span className="font-semibold text-sm">Menú</span>
                            <button onClick={closeMenu} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-secondary transition-colors">
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4">
                            {userBlock}
                            {navItems}
                            {logoutBtn}
                        </div>
                    </div>
                </>
            )}

            {/* ── Main layout ── */}
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-6">
                {/* Desktop top bar */}
                <div className="hidden lg:flex items-center justify-between mb-6">
                    <Link to="/" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                        <ArrowLeft className="h-4 w-4" />Volver al marketplace
                    </Link>
                    <div className="flex items-center gap-2">
                        <LanguageSwitcher />
                        <Button size="sm" onClick={() => nav('/dashboard/ads/new')} data-testid="dash-new-ad">
                            <Plus className="h-4 w-4 mr-1" />{t('nav.publish','Publicar')}
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-6">
                    {/* Desktop sidebar */}
                    <aside className="hidden lg:block col-span-3">
                        <div className="rounded-2xl border bg-card p-4 sticky top-6">
                            {userBlock}
                            {navItems}
                            {logoutBtn}
                        </div>
                    </aside>

                    {/* Content */}
                    <section className="col-span-12 lg:col-span-9">
                        <Outlet />
                    </section>
                </div>
            </div>
        </div>
    );
}
